import { ArrowLeft, MessageSquareWarning, Minus, Plus } from 'lucide-preact';
import type { TargetedEvent } from 'preact/compat';
import { useEffect } from 'preact/hooks';

import { useCustomerLoyalty } from '@/context/LoyaltyContext';
import { useNavigation } from '@/context/NavigationContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { formatNumberWithCommas } from '@/helpers/functions/number';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { E_AppRoutes } from '@/types/Routes';
import { Input } from '@/ui';

export function FlexiblePointsRedemption() {
  const { customerLoyalty, pointsToRedeem, setPointsToRedeem } = useCustomerLoyalty();
  if (!customerLoyalty) return null;
  const { programs, basicInfo } = customerLoyalty;
  const { currency } = basicInfo;
  const { flexiblePoints } = programs;
  const { availablePoints, exchangeRate, pointsBalanceTitleEn } = flexiblePoints;
  const { navigateTo } = useNavigation();

  const { theme } = useWidgetConfig();

  const { backgroundColor = COLORS.BRAND_SECONDARY, textColor = COLORS.TEXT_INVERSE } =
    theme?.secondaryButton ?? {};

  const { backgroundColor: hoverBackground, hoverHandlers } = useHoverBackground({
    baseColor: backgroundColor,
  });

  const {
    backgroundColor: primaryButtonBackgroundColor = COLORS.BRAND_PRIMARY,
    textColor: primaryButtonTextColor = COLORS.TEXT_INVERSE,
  } = theme?.primaryButton ?? {};

  const {
    backgroundColor: primaryButtonHoverBackground,
    hoverHandlers: primaryButtonHoverHandlers,
  } = useHoverBackground({
    baseColor: primaryButtonBackgroundColor,
  });

  const iconColor = theme?.bodyColor;

  const cashValue = pointsToRedeem / exchangeRate;
  const maxRedeemable = availablePoints;

  const handleIncrement = () => {
    if (pointsToRedeem < maxRedeemable) {
      setPointsToRedeem((prev) => Math.min(prev + 50, maxRedeemable));
    }
  };

  const handleDecrement = () => {
    if (pointsToRedeem > 50) {
      setPointsToRedeem((prev) => Math.max(prev - 50, 50));
    }
  };

  const handleInputChange = (e: TargetedEvent<HTMLInputElement, Event>) => {
    const value = parseInt((e.currentTarget as HTMLInputElement).value) || 0;
    if (value >= 0 && value <= maxRedeemable) {
      setPointsToRedeem(value);
    }
  };

  const onBack = () => navigateTo(E_AppRoutes.REGISTERED_CUSTOMER);

  const handleRedeem = () => navigateTo(E_AppRoutes.REDEMPTION);

  const quickAmounts = [
    { label: '25%', points: Math.floor(maxRedeemable * 0.25) },
    { label: '50%', points: Math.floor(maxRedeemable * 0.5) },
    { label: '75%', points: Math.floor(maxRedeemable * 0.75) },
    { label: '100%', points: maxRedeemable },
  ];

  const isPointsToRedeemEmpty = pointsToRedeem === 0;
  const customerHasNoPoints = availablePoints === 0;

  useEffect(() => {
    if (isPointsToRedeemEmpty) setPointsToRedeem(availablePoints / 2);
  }, []);

  return (
    <div className='py-4'>
      <button
        onClick={onBack}
        className='mb-6 flex items-center space-x-2 border-none bg-transparent text-gray-600 outline-0 transition-colors hover:text-gray-900'
      >
        <ArrowLeft className='h-4 w-4' />
        <span className='text-sm'>Back</span>
      </button>

      <div className='mb-6 text-center'>
        {customerHasNoPoints && (
          <div className='mb-3 space-y-2'>
            <div className='flex items-center justify-start gap-3 rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-700'>
              <MessageSquareWarning className='mt-[2px] h-4 w-4' />
              <h5>You don't have enough points</h5>
            </div>
          </div>
        )}

        <h3 className='mb-2'>Redeem {pointsBalanceTitleEn}</h3>
        <h5>
          You have{' '}
          <span className='font-semibold'>
            {formatNumberWithCommas(availablePoints)} {pointsBalanceTitleEn?.toLocaleLowerCase()}
          </span>{' '}
          available
        </h5>
      </div>

      <div className='mb-6 space-y-4'>
        <div className='flex items-center justify-center space-x-4'>
          <button
            onClick={handleDecrement}
            disabled={customerHasNoPoints}
            className='flex h-10 w-10 items-center justify-center rounded-full border-none bg-gray-100 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50'
          >
            <Minus className='h-4 w-4' style={{ color: iconColor }} />
          </button>

          <div className='max-w-32 flex-1'>
            <Input
              type='number'
              value={pointsToRedeem}
              onChange={handleInputChange}
              className='border-none text-center text-lg font-semibold disabled:cursor-not-allowed'
              min='0'
              max={maxRedeemable}
              disabled={customerHasNoPoints}
              step='1'
            />
          </div>

          <button
            onClick={handleIncrement}
            disabled={customerHasNoPoints}
            className='flex h-10 w-10 items-center justify-center rounded-full border-none bg-gray-100 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50'
          >
            <Plus className='h-4 w-4' style={{ color: iconColor }} />
          </button>
        </div>

        <div className='text-center'>
          <h1>
            {currency} {formatNumberWithCommas(cashValue)}
          </h1>
          <h5>Account Credit</h5>
        </div>
      </div>

      <div className='mb-6 grid grid-cols-4 gap-2'>
        {quickAmounts.map((amount) => {
          const isSelected = amount?.points === pointsToRedeem;

          return (
            <button
              {...hoverHandlers}
              key={amount.label}
              onClick={() => setPointsToRedeem(amount.points)}
              disabled={isPointsToRedeemEmpty}
              className={`secondary-button rounded-lg border-none px-3 py-2 text-sm font-medium outline-0 transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
              style={{
                backgroundColor: isSelected ? hoverBackground : undefined,
                color: isSelected ? textColor : iconColor,
              }}
            >
              {amount.label}
            </button>
          );
        })}
      </div>

      <div className='mb-6 rounded-lg border p-3'>
        <h5 className='text-center'>
          Exchange Rate: 1 {pointsBalanceTitleEn} = {currency}{' '}
          {formatNumberWithCommas(1 / exchangeRate)}
        </h5>
      </div>

      <button
        onClick={handleRedeem}
        {...primaryButtonHoverHandlers}
        disabled={isPointsToRedeemEmpty || pointsToRedeem > maxRedeemable}
        style={{ backgroundColor: primaryButtonHoverBackground, color: primaryButtonTextColor }}
        className='w-full rounded-lg px-4 py-3 font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'
      >
        Next
      </button>
    </div>
  );
}
